import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  makeStyles,
  Divider,
  TextField,
  InputAdornment,
  FormControl
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { cloneDeep, get, set } from 'lodash';
import ClearIcon from '@material-ui/icons/Clear';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';

import { DatePickerRangeComponent } from 'src/components';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import {
  dateToday,
  downloadCSV,
  formatDate,
  toUtcEquivalentDate
} from 'src/utils';
import {
  BranchMeta,
  CustomInputEvent,
  DateFields,
  GenericMenuOptionV2,
  InventoryAction,
  InventoryKeywordInputField,
  ManufacturerOption
} from 'src/types';
import { ListingStatusEnum } from 'src/enums';
import { Autocomplete } from '@material-ui/lab';

import { multiBranchFeat } from 'src/constants/feature-toggle';
import { BranchListDropDown } from 'src/components/dropdown';
import { usePermissions, useSnackBar } from 'src/hooks';
import { unwrapResult } from '@reduxjs/toolkit';
import { StatusDropdown } from './StatusDropdown';

interface Props {
  className?: string;
  manufacturers?: ManufacturerOption[];
  isLoading: boolean;
  hasStatus: boolean;
  userBranchOptions: BranchMeta[];
  selectedBranchIDs: number[];
  handleBranchChange: (v: any) => void;
  onApplyFilterPress: (val: string) => void;
  handleSetManufacturer: (id?: number) => void;
}

const {
  actions: categoryActions,
  selectors: categorySelectors
} = slices.category;

const {
  actions: inventoryActions,
  selectors: inventorySelectors
} = slices.inventory;

const useStyles = makeStyles((theme) => ({
  root: {},
  filterTitle: {
    marginBottom: theme.spacing(1)
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  buttonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  applyBtn: {
    marginLeft: theme.spacing(5)
  }
}));

const InventoryFilter = ({
  className,
  hasStatus,
  selectedBranchIDs,
  manufacturers,
  onApplyFilterPress,
  handleBranchChange,
  handleSetManufacturer,
  ...rest
}: Props) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const snackbar = useSnackBar();
  const {
    viewTransferListingStock: canViewTransfer,
    canExtractNvidiaBuyIn
  } = usePermissions();
  const inventoryFilter = useAppSelector(
    inventorySelectors.selectAllInventoryFilter
  );

  const categories = useAppSelector(categorySelectors.selectCategories);

  const [manufacturerVal, setManufacturerVal] = useState<string>('');
  const [categVal, setCategVal] = useState<string>('');

  const onManufacturerChange = useCallback(
    (val: string) => {
      const manufacturer = manufacturers?.find((i) => i.label === val);
      const id = manufacturer?.id;
      handleSetManufacturer(id);
      setManufacturerVal(manufacturer?.label ?? val);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [manufacturers]
  );

  const getExtractedNvidiaBuyIn = useCallback(async () => {
    const from = inventoryFilter?.encoded_date?.from ?? null;
    const to = inventoryFilter?.encoded_date?.to ?? null;
    if (!from) {
      console.error(
        'Error: Invalid date range filter for getting nvidia buy in',
        inventoryFilter?.encoded_date
      );
      snackbar.show({
        severity: 'error',
        message:
          'The date encoded from for fetching Nvidia Buy-In data is invalid.'
      });
      return;
    }
    const res = unwrapResult(
      await dispatch(
        inventoryActions.getExtractedNvidiaBuyIn({ from: from, to: to })
      )
    );

    if (res.success) {
      const nvidiaBuyIn = res?.originalData?.data;
      downloadCSV(
        nvidiaBuyIn,
        `NVIDIA BUY IN ${formatDate(
          inventoryFilter?.encoded_date?.from
        )} TO ${formatDate(inventoryFilter?.encoded_date?.from)}`
      );
    } else {
      const { errors, message } = res;
      const errorMessages: string[] = Object.values(errors);

      snackbar.show({
        severity: 'error',
        message:
          errorMessages?.join(', ') ??
          message ??
          'Failed to get nivdia buy ins, please contact support'
      });
    }
  }, [dispatch, inventoryFilter, snackbar]);

  const onInternalApply = useCallback((val: InventoryAction) => {
    onApplyFilterPress(val);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getCategories = () => {
    dispatch(categoryActions.getCategoriesThunk({}));
  };

  const getDateKeyObj = (field: DateFields) => {
    let key: 'sold_date' | 'encoded_date' | 'purchase_date' | '' = '';
    let rangeKey: 'to' | 'from' | '' = '';

    if (field.includes('Purchase')) {
      key = 'purchase_date';
    }
    if (field.includes('Sold')) {
      key = 'sold_date';
    }
    if (field.includes('Encoded')) {
      key = 'encoded_date';
    }
    if (field.includes('To')) {
      rangeKey = 'to';
    }
    if (field.includes('From')) {
      rangeKey = 'from';
    }
    return `${key}.${rangeKey}`;
  };

  const onChangeInputKeyword = useCallback(
    (e: CustomInputEvent, field: InventoryKeywordInputField) => {
      const { value } = e.target;
      dispatch(inventoryActions.onChangeInput({ keyword: value, field }));
    },
    [dispatch]
  );

  const onChangeCategory = useCallback((val: number | null) => {
    dispatch(
      inventoryActions.updateCategoryFilter({ category_id: val ?? null })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterTransformerViaDateField = useCallback(
    (date: MaterialUiPickersDate, field: DateFields) => {
      const filterCopy = inventoryFilter ? cloneDeep(inventoryFilter) : {};
      const dateUtc = toUtcEquivalentDate(date)?.toISOString();
      const finalKey = getDateKeyObj(field);
      set(filterCopy, finalKey, dateUtc);
      return filterCopy;
    },
    [inventoryFilter]
  );

  const onChangedDate = useCallback(
    (date: MaterialUiPickersDate, field: DateFields) => {
      const newFilter = filterTransformerViaDateField(date, field);
      dispatch(inventoryActions.updateFilter(newFilter));
    },
    [dispatch, filterTransformerViaDateField]
  );

  const onPressClear = useCallback(() => {
    setManufacturerVal('');
    setCategVal('');
    dispatch(inventoryActions.resetFilter());
    handleBranchChange([]); // intended to call last
  }, [dispatch, handleBranchChange]);

  const dateValue = useCallback(
    (field: DateFields) => {
      const key = getDateKeyObj(field);
      const date = get(inventoryFilter, key);
      return date ?? null;
    },
    [inventoryFilter]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onChangeStatus = (
    statusOptions: GenericMenuOptionV2 | GenericMenuOptionV2[]
  ) => {
    dispatch(inventoryActions.onInventoryChangeStatus(statusOptions));
  };

  useEffect(() => {
    if (!canViewTransfer) {
      dispatch(
        inventoryActions.onToggleStatus({
          value: ListingStatusEnum.Transfer,
          isCheck: false
        })
      );
    }
  }, [canViewTransfer, dispatch]);

  useEffect(() => {
    getCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const RenderInputFilters = useCallback(() => {
    return (
      <div>
        <Typography className={classes.filterTitle} align="center" variant="h5">
          General Information
        </Typography>
        <Grid container spacing={2} alignItems="center" justify="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              value={inventoryFilter?.product_name || ''}
              label="Product Name"
              name="product_name"
              onChange={(e) => onChangeInputKeyword(e, 'product_name')}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Serial No"
              name="serial_no"
              value={inventoryFilter?.serial_no || ''}
              onChange={(e) => onChangeInputKeyword(e, 'serial_no')}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              value={inventoryFilter?.supplier_name || ''}
              label="Supplier Name"
              name="supplier_name"
              onChange={(e) => onChangeInputKeyword(e, 'supplier_name')}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              value={inventoryFilter?.dr_no || ''}
              label="DR No."
              name="dr_no"
              onChange={(e) => onChangeInputKeyword(e, 'dr_no')}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              fullWidth
              inputValue={categVal}
              onInputChange={(_, newInputValue) => {
                const chosenCategory = categories?.find(
                  (i) => i?.name === newInputValue
                );
                onChangeCategory(chosenCategory?.id ?? null);
                setCategVal(newInputValue);
              }}
              style={{ minWidth: 250 }}
              id="category"
              options={categories}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <TextField {...params} label="Category" variant="outlined" />
              )}
            />
          </Grid>
          {multiBranchFeat ? (
            <Grid item md={4} xs={12}>
              <BranchListDropDown
                defaultValue={selectedBranchIDs}
                multiple
                onHandleBranchChange={(branchId?: number[]) => {
                  handleBranchChange(branchId);
                }}
              />
            </Grid>
          ) : null}
          <Grid item md={2} xs={12}>
            {manufacturers?.[0] && (
              <FormControl variant="outlined" fullWidth>
                <Autocomplete
                  disablePortal
                  fullWidth
                  inputValue={manufacturerVal}
                  onInputChange={(_, newVal) => onManufacturerChange(newVal)}
                  options={manufacturers}
                  getOptionLabel={(option) => option.label}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={'Manufacturer'}
                      variant="outlined"
                    />
                  )}
                />
              </FormControl>
            )}
          </Grid>
          <Grid item md={2} xs={12}>
            <StatusDropdown
              handleOptionChange={(
                options: GenericMenuOptionV2 | GenericMenuOptionV2[]
              ) => onChangeStatus(options)}
            />
          </Grid>
        </Grid>
      </div>
    );
  }, [
    classes,
    inventoryFilter,
    manufacturers,
    selectedBranchIDs,
    manufacturerVal,
    categVal,
    categories,
    onChangeInputKeyword,
    onChangeCategory,
    onManufacturerChange,
    handleBranchChange,
    onChangeStatus
  ]);

  const RenderButtonsGroup = useCallback(() => {
    return (
      <Grid
        container
        alignItems="center"
        justify="space-between"
        style={{ gap: '1em' }}
      >
        <Grid item className={classes.buttonsContainer}>
          <Grid item>
            <Button
              endIcon={<ClearIcon />}
              onClick={onPressClear}
              color="secondary"
              variant="outlined"
            >
              Set To Default
            </Button>
          </Grid>
        </Grid>
        <Grid
          item
          container
          style={{ gap: '1em', width: canExtractNvidiaBuyIn ? '40em' : '20em' }}
        >
          <Grid item sm={canExtractNvidiaBuyIn ? 6 : 12} xs={12}>
            <Button
              fullWidth
              disabled={!hasStatus}
              endIcon={<SearchIcon />}
              onClick={() => onInternalApply('computation')}
              color="primary"
              variant="contained"
              style={{ height: 'inherit' }}
            >
              APPLY FILTER
            </Button>
          </Grid>
          {canExtractNvidiaBuyIn ? (
            <Grid item sm xs={12}>
              <Button
                fullWidth
                disabled={!canExtractNvidiaBuyIn}
                onClick={getExtractedNvidiaBuyIn}
                color="primary"
                variant="outlined"
              >
                Extract NVIDIA Buy In
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
    );
  }, [
    classes,
    hasStatus,
    onInternalApply,
    onPressClear,
    canExtractNvidiaBuyIn,
    getExtractedNvidiaBuyIn
  ]);

  const RenderDatePickersFilter = useCallback(() => {
    return (
      <Grid container spacing={4} alignItems="center" justify="center">
        <Grid item>
          <DatePickerRangeComponent
            title="Date Sold (Customer)"
            toDateValue={dateValue('dateSoldTo')}
            fromDateValue={dateValue('dateSoldFrom')}
            fromDateMax={dateToday()}
            toDateMin={inventoryFilter?.sold_date?.from}
            dateToLabel="Date Sold (To)"
            dateFromLabel="Date Sold (From)"
            onChangeToDate={(date) => onChangedDate(date, 'dateSoldTo')}
            onChangeFromDate={(date) => onChangedDate(date, 'dateSoldFrom')}
          />
        </Grid>
        <Grid item>
          <DatePickerRangeComponent
            title="Date Purchased  (Supplier)"
            toDateValue={dateValue('datePurchasedTo')}
            fromDateValue={dateValue('datePurchasedFrom')}
            fromDateMax={dateToday()}
            toDateMin={inventoryFilter?.purchase_date?.from}
            dateToLabel="Date Purchased (To)"
            dateFromLabel="Date Purchased (From)"
            onChangeToDate={(date) => onChangedDate(date, 'datePurchasedTo')}
            onChangeFromDate={(date) =>
              onChangedDate(date, 'datePurchasedFrom')
            }
          />
        </Grid>
        <Grid item>
          <DatePickerRangeComponent
            title={
              canExtractNvidiaBuyIn
                ? 'Date Encoded (Includes Nvidia Buy In)'
                : 'Date Encoded'
            }
            toDateValue={dateValue('dateEncodedTo')}
            fromDateValue={dateValue('dateEncodedFrom')}
            fromDateMax={dateToday()}
            toDateMin={inventoryFilter?.encoded_date?.from}
            dateToLabel="Date Encoded (To)"
            dateFromLabel="Date Encoded (From)"
            onChangeToDate={(date) => onChangedDate(date, 'dateEncodedTo')}
            onChangeFromDate={(date) => onChangedDate(date, 'dateEncodedFrom')}
          />
        </Grid>
      </Grid>
    );
  }, [dateValue, onChangedDate, inventoryFilter, canExtractNvidiaBuyIn]);

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Box display="flex" flexDirection="row" justifyContent="space-between">
        <Typography color="textPrimary" gutterBottom variant="h3">
          Inventory
        </Typography>
      </Box>
      <Box mt={3}>
        <Card>
          <CardContent>
            {RenderInputFilters()}
            <Divider className={classes.divider} />
            {RenderDatePickersFilter()}
            <Divider className={classes.divider} />
            {RenderButtonsGroup()}
          </CardContent>
        </Card>
      </Box>
    </div>
  );
};

export default InventoryFilter;
